import { useEffect, useMemo, useState } from 'react';
import turfBbox from '@turf/bbox';
import { atom, useAtomValue } from 'jotai';
import type { MapRef } from 'react-map-gl';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { dpsMapConfigAtom } from './atoms';
import Map from 'components/map/map';
import { PolygonArea } from 'components/map';
import { buildPolygonStyle } from 'lib/map';
import type { AreaFeature } from './types';
import styles from './scenario-map.module.scss';

const DEFAULT_CENTER = { longitude: -99.25, latitude: 31.25 };
const DEFAULT_ZOOM = 5;

const scenarioMapRefAtom = atom<MapRef | null>(null);

const BASE_STYLE = buildPolygonStyle({
  color: '#a8a8a8',
  fillOpacity: 0.15,
  lineOpacity: 0.6,
  lineWidth: 1,
});

const FOCUSED_STYLE = buildPolygonStyle({
  color: '#0f62fe',
  fillOpacity: 0.3,
  lineOpacity: 0.8,
  lineWidth: 2,
});

interface ScenarioMapProps {
  /**
   * Readonly, and reference-stable by contract: the caller must only hand over
   * a new array when the set of geometries to draw has actually changed, since
   * that reference is what re-fits the camera (INS-12060). `getMemoizedMapAreas`
   * is what upholds that.
   */
  areas: readonly AreaFeature[];
  focusedId: number | null;
}

/**
 * Mapbox GL map displaying area geometries with focused-area highlighting.
 * All areas are always visible; the focused area is highlighted blue,
 * others are shown in muted grey.
 * @param {ScenarioMapProps} props - The component props
 * @return {JSX.Element} The map accordion
 */
export function ScenarioMap({
  areas,
  focusedId,
}: ScenarioMapProps): JSX.Element {
  const [expanded, setExpanded] = useState(true);
  const mapRef = useAtomValue(scenarioMapRefAtom);

  const bounds = useMemo(() => {
    if (areas.length === 0) return null;
    const collection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: areas.map((a) => ({
        type: 'Feature' as const,
        properties: {},
        geometry: a.geometry,
      })),
    };
    const [minLng, minLat, maxLng, maxLat] = turfBbox(collection);
    return [
      [minLng, minLat],
      [maxLng, maxLat],
    ] as [[number, number], [number, number]];
  }, [areas]);

  useEffect(() => {
    if (!mapRef || !bounds) return;
    mapRef.getMap().fitBounds(bounds, { padding: 40, maxZoom: 15 });
  }, [mapRef, bounds]);

  return (
    <Accordion
      expanded={expanded}
      onClick={() => setExpanded(!expanded)}
      className={styles.accordion}
    >
      <AccordionSummary>
        <h4 className={styles.mapTitle}>Map</h4>
      </AccordionSummary>
      <AccordionDetails>
        <div className={styles.mapContainer}>
          <Map
            className={styles.mapFill}
            longitude={DEFAULT_CENTER.longitude}
            latitude={DEFAULT_CENTER.latitude}
            zoom={DEFAULT_ZOOM}
            mapConfigAtom={dpsMapConfigAtom}
            refAtom={scenarioMapRefAtom}
            showControls
            showMapFilters
          >
            {areas.map((area) => (
              <PolygonArea
                key={area.id}
                id={`scenario-area-${area.id}`}
                geometry={area.geometry}
                style={
                  focusedId === null || area.id === focusedId
                    ? FOCUSED_STYLE
                    : BASE_STYLE
                }
              />
            ))}
          </Map>
        </div>
      </AccordionDetails>
    </Accordion>
  );
}
